import { useCallback } from 'react';
import type {
  ReactElement,
  SyntheticEvent,
  ReactNode,
} from 'react';

import styled from 'styled-components';

import BaseModal from 'react-overlays/Modal';
import type {
  ModalProps as BaseModalProps,
} from 'react-overlays/Modal';

import { ModalBody } from './ModalBody';
import { ModalDialog } from './ModalDialog';
import { ModalFooter } from './ModalFooter';
import { ModalTitle } from './ModalTitle';
import { renderBackdrop } from './renderBackdrop';

import { modalSizes } from './modalSizes';
import type {
  ModalSize,
} from './types';

export { modalSizes };
export type {
  ModalSize,
};

export const StyledModal = styled(BaseModal)({
  position: 'fixed',
  zIndex: 1040,
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
});

export const StyledDialogWrapper = styled.div({
  height: '100vh',
  overflowY: 'auto',
  outline: 'none',
  position: 'relative',
});

export type ModalProps = Omit<BaseModalProps, 'children'> & {
  /**
   * Размер модального окна
   */
  size?: ModalSize;
  /**
   * Отображается ли модальное окно
   */
  show: boolean;
  /**
   * Отображается ли кнопка закрытия окна
   */
  hasCloseButton?: boolean;
  /**
   * Обработчик закрытия модального окна
   */
  onHide: () => void;
  /**
   * Закрывать ли окно при нажатии вне окна
   */
  isHideOnBackdropClick?: boolean;
  /**
   * Содержимое окна
   */
  children?: ReactNode | ReactNode[];
};

export function Modal({
  size = modalSizes.DEFAULT,
  show,
  hasCloseButton = true,
  children = undefined,
  onHide,
  isHideOnBackdropClick = true,
  ...rest
}: ModalProps): ReactElement {
  const onWrapperClick = useCallback((event: SyntheticEvent) => {
    if (isHideOnBackdropClick && event.target === event.currentTarget) {
      onHide();
    }
  }, [onHide, isHideOnBackdropClick]);

  return (
    <StyledModal
      show={show}
      renderBackdrop={renderBackdrop}
      onHide={onHide}
      {...rest}
    >
      {/* eslint-disable jsx-a11y/click-events-have-key-events */}
      <StyledDialogWrapper
        onClick={onWrapperClick}
        role="button"
        tabIndex={0}
      >
        {/* eslint-enable jsx-a11y/click-events-have-key-events */}
        <ModalDialog
          size={size}
          onHide={onHide}
          hasCloseButton={hasCloseButton}
        >
          {children}
        </ModalDialog>
      </StyledDialogWrapper>
    </StyledModal>
  );
}

Modal.Body = ModalBody;

Modal.Footer = ModalFooter;

Modal.Title = ModalTitle;
